import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import { useCancelSubscription } from "@/queries/organization-queries";
import { SubscriptionType } from "gitpod-next-api/gitpod/v1/billing_pb";
import { useCallback, type FC } from "react";

type CancelSubscriptionModalProps = {
    onClose: () => void;
};

export const CancelSubscriptionModal: FC<CancelSubscriptionModalProps> = ({ onClose }) => {
    const cancelSubscription = useCancelSubscription();

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleCancelSubscription = useCallback(async () => {
        try {
            await cancelSubscription.mutateAsync(SubscriptionType.CORE);

            onClose();
        } catch (error) {
            console.error("Failed to cancel subscription:", error);
        }
    }, [cancelSubscription, onClose]);

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-track-location={TrackLocations.BillingModal}>
                <DialogHeader>
                    <DialogTitle>We&apos;re sorry to see you go</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <Text>
                        Are you sure you&apos;d like to cancel your Core plan? We understand that circumstances change,
                        and we want to make this as easy as possible for you.
                    </Text>
                    <Text>What happens next:</Text>
                    <ul className="list-inside list-disc space-y-1 font-normal">
                        <li>Core features stay active until the end of your current billing period</li>
                        <li>After that, your account will continue with our free plan features.</li>
                        <li>You can always resubscribe anytime if your needs change</li>
                    </ul>
                    <Text>Is there anything we could have done better? We&apos;d love to hear your feedback.</Text>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            data-tracking-id="keep-plan-cancel-subscription-modal"
                        >
                            Keep Plan
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={cancelSubscription.isPending}
                        onClick={handleCancelSubscription}
                        data-tracking-id="cancel-plan-cancel-subscription-modal"
                    >
                        Cancel Plan
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
